import { ChangeDetectionStrategy, Component, HostListener, OnInit, inject } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';

import { BehaviorSubject, Observable, combineLatest } from 'rxjs';

import { ActionDisabledCondition } from '@mediahaven/shared';
import {
    ActionItem,
    ApiFragment,
    ApiRecordType,
    BaseContainer,
    DialogConfig,
    DialogRef,
    FormRadioListItem,
    FormSelectOption,
    MediaObjectId,
    MultiFormSelectOptions,
    RecordId,
    RecordStatus,
    RecordType,
    RecordTypeTranslationService,
    RecordTypesService,
    UserFunction,
    getRecordTypeOptionLabel,
    isNotNullOrUndefined
} from '@stylehaven/components';
import {
    debounceTime,
    distinctUntilChanged,
    map,
    shareReplay,
    startWith,
    switchMap,
    takeUntil,
    withLatestFrom
} from 'rxjs/operators';
import {
    SelectParentDialogOutput,
    SelectParentDialogOutputCancel,
    SelectParentDialogOutputSuccess,
    SelectParentDialogType
} from '../../../entities/dialog/select-parent-dialog.entity';
import { FragmentsSandbox } from '../../records/sandbox/fragments.sandbox';

@Component({
    standalone: false,
    selector: 'mhv-select-parent-dialog',
    templateUrl: './select-parent-dialog.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SelectParentDialogComponent extends BaseContainer implements OnInit {
    public readonly SEARCH_LIST_LIMIT: number = 50;

    public recordTypeOptions: FormRadioListItem[];
    public selectedRecordType$: Observable<RecordType>;
    public selectedRecordTypeTranslation$: Observable<string>;

    public parentOptions$: Observable<MultiFormSelectOptions<ApiFragment>>;
    public searchTerm$: BehaviorSubject<string> = new BehaviorSubject('');
    public dialogActionItems$: Observable<ActionItem[]>;
    public disableDropdown$: Observable<boolean>;

    private canSubmit$: Observable<boolean>;
    private canPublish$: Observable<boolean>;

    public parentSelectionFormGroup: UntypedFormGroup;

    private childRecordTypes: RecordType[];
    public translatedChildRecordType: string;

    private classificationId: MediaObjectId;
    private recordsToOmit: RecordId[];

    private dialogType: SelectParentDialogType;

    private readonly recordTypeTranslationService: RecordTypeTranslationService = inject(RecordTypeTranslationService);

    constructor(
        public config: DialogConfig,
        public dialog: DialogRef<SelectParentDialogOutput>,
        private readonly formBuilder: UntypedFormBuilder,
        private readonly recordTypeService: RecordTypesService,
        private readonly fragmentSandbox: FragmentsSandbox
    ) {
        super();
    }

    @HostListener('document:keydown.enter', ['$event'])
    handleEnter(event: KeyboardEvent) {
        // Prevent submitting when form is invalid or focus is not on dialog
        if (!this.parentSelectionFormGroup.valid) {
            return;
        }
        event.preventDefault();
        this.submit();
    }

    ngOnInit(): void {
        this.parseConfig();
        this.initForm();
        this.initUserFunctionStreams();
        this.initRecordTypes();
        this.initParentOptions();
    }

    private parseConfig(): void {
        this.childRecordTypes = this.config.data.recordTypes ?? [];
        this.classificationId = this.config.data.classificationId;
        this.recordsToOmit = this.config.data.recordsToOmit ?? [];
        this.dialogType = this.config.data.dialogType;
    }

    private initForm(): void {
        this.parentSelectionFormGroup = this.formBuilder.group({
            recordType: [null, Validators.required],
            parent: [null, Validators.required]
        });
    }

    private initUserFunctionStreams(): void {
        this.canSubmit$ = this.fragmentSandbox.hasUserFunction(UserFunction.submitMedia);
        this.canPublish$ = this.fragmentSandbox.hasUserFunction(UserFunction.publishMedia);
    }

    private initRecordTypes(): void {
        const parentTypes: ApiRecordType[] =
            this.childRecordTypes.length > 0
                ? this.recordTypeService.getParentsOfTypes(this.childRecordTypes)
                : this.recordTypeService.getParentTypes();

        const locale = this.translateService.getCurrentLocale();

        this.recordTypeOptions = parentTypes.map(recordType => ({
            id: recordType.Name,
            value: recordType.Name,
            label: getRecordTypeOptionLabel(recordType, locale, parentTypes)
        }));

        this.recordTypeControl.setValue(this.recordTypeOptions[0].value);

        this.selectedRecordType$ = this.recordTypeControl.valueChanges.pipe(
            startWith(this.recordTypeControl.value),
            isNotNullOrUndefined(),
            distinctUntilChanged()
        );

        this.selectedRecordTypeTranslation$ = this.selectedRecordType$.pipe(
            map(recordType => this.recordTypeTranslationService.getRecordTypeTranslation(recordType))
        );

        if (this.childRecordTypes.length === 1) {
            this.translatedChildRecordType = this.recordTypeTranslationService.getRecordTypeTranslation(
                this.childRecordTypes[0]
            );
        }
    }

    private initParentOptions(): void {
        const search$: Observable<string> = this.searchTerm$.pipe(debounceTime(500), distinctUntilChanged());
        // if one of two obs emits value, then will trigger, once both have value
        this.parentOptions$ = combineLatest([this.selectedRecordType$, search$]).pipe(
            // NOTE: switchMap is to create a new observable,
            switchMap(([recordType, searchTerm]) =>
                // NOTE: then to trigger to search
                //  to avoid obs wrap inside obs - TMY THINK LATER
                this.fragmentSandbox.getRecordsOfTypeAsFormSelectOptions(searchTerm, recordType, this.recordsToOmit)
            ),
            // NOTE: obs is good for - observing changes - ex: user changes sth
            withLatestFrom(this.canSubmit$, this.canPublish$),
            map(([multiFormSelectOptions, canSubmit, canPublish]) =>
                this.disableParentOptions(multiFormSelectOptions, canSubmit, canPublish)
            ),
            shareReplay(1)
        );

        this.selectedRecordType$.pipe(takeUntil(this.destroy$)).subscribe(_ => {
            this.parentControl.setValue(null);
        });
    }

    submit(): void {
        const parentRecord: ApiFragment = this.parentControl.value[0];
        const output: SelectParentDialogOutputSuccess = {
            type: 'success',
            parentRecord
        };

        this.dialog.close(output);
    }

    cancel(): void {
        const output: SelectParentDialogOutputCancel = {
            type: 'cancel'
        };
        this.dialog.close(output);
    }

    private disableParentOptions(
        multiFormSelectOptions: MultiFormSelectOptions<ApiFragment>,
        canSubmit: boolean,
        canPublish: boolean
    ): MultiFormSelectOptions<ApiFragment> {
        return {
            ...multiFormSelectOptions,
            options: multiFormSelectOptions.options.map(option =>
                this.setParentOptionState(option, canSubmit, canPublish)
            )
        };
    }

    /* Disabled when:
     *  * Parent option is published and user does not have Submit or Publish rights
     *  * Parent option is submitted
     *  * Parent option has a different classification than the provided children
     */
    private setParentOptionState(
        option: FormSelectOption<ApiFragment>,
        canSubmit: boolean,
        canPublish: boolean
    ): FormSelectOption<ApiFragment> {
        const disabledConditions: ActionDisabledCondition[] = [
            {
                condition:
                    this.dialogType !== SelectParentDialogType.ADD &&
                    option.value.isPublished() &&
                    !canSubmit &&
                    !canPublish,
                tooltip: this.translateService.translate('intellectual.tooltips.insufficient-rights', {
                    amount: 1
                })
            },
            {
                condition: option.value.getRecordStatus() === RecordStatus.Submitted,
                tooltip: this.translateService.translate('intellectual.tooltips.submitted', {
                    amount: 1
                })
            },
            {
                condition: !!this.classificationId && option.value.getClassificationId() !== this.classificationId,
                tooltip: this.translateService.translate('intellectual.tooltips.wrong-classification')
            }
        ];

        const disabled: boolean = disabledConditions.some(disabledCondition => disabledCondition.condition);

        const disabledInfoMsg: string | undefined = disabledConditions.find(
            disabledCondition => disabledCondition.condition
        )?.tooltip;

        return {
            ...option,
            disabled,
            disabledInfoMsg
        };
    }

    public onSearchTermChange(searchTerm: string) {
        this.searchTerm$.next(searchTerm);
    }

    get recordTypeControl(): UntypedFormControl {
        return this.parentSelectionFormGroup.get('recordType') as UntypedFormControl;
    }

    get parentControl(): UntypedFormControl {
        return this.parentSelectionFormGroup.get('parent') as UntypedFormControl;
    }

    get childCount(): number {
        return this.childRecordTypes.length;
    }
}
